<!-- 
  @file SubmissionType.vue
  @description
  Handles the submission type selection step of the finance application process.
  Features:
  - Submission type selection (Finance New Car/Refinance Current Car)
  - Form validation
  - Navigation to next step
  - Accessibility support
  - Standardized error handling via useStoreOperation
  - Loading state management
  - User-friendly error messages
  @component
-->
<template>
  <PageLayout current-page="submission-type" :header="header" :showBenefits="isDefaultTheme">
    <ButtonGroup
      class="mt-4"
      :name="'submission-type'"
      :items="submissionOptions"       
      :model-value="values.submissionType"
      @update:model-value="handleSelection"
      :disabled="isNavigating || isLoading || isProcessingSelection"
      ref="buttonGroupRef"
      :selected-value="values.submissionType"
    />
  </PageLayout>
</template>
  
<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import { object, number } from 'zod';
import { useRootStore } from '@/stores/rootStore';
import { createElementId, Page, ElementType } from '@/utils/elementIds';
import type { StoreState } from '@/types/storeTypes';
import { useStoreOperation } from '@/composables/useStoreOperation';
import { useConfigStore } from '@/stores/modules/configStore';
import ButtonGroup from '@/components/ButtonGroup.vue';
import { usePageNavigation } from '@/composables/usePageNavigation';
import PageLayout from '@/layouts/PageLayout.vue';
import { useRouter } from 'vue-router';

const store = useRootStore();
const configStore = useConfigStore();
const buttonGroupRef = ref<InstanceType<typeof ButtonGroup> | null>(null);
const isDefaultTheme = computed(() => !configStore.isDefaultTheme);
const router = useRouter();

// Replace config content with hardcoded values
const SUBMISSION_TYPE_CONTENT = {
  HEADERS: {
    TITLE: 'What would you like a quote for?'
  },
  OPTIONS: [
    { label: 'Finance My Next Car', value: 0 },
    { label: 'Refinance My Current Car', value: 1 }
  ],
  ERROR_MESSAGE: 'Please select an option to continue',
  VALIDATION: {
    OPTION_REQUIRED: 'Please select an option'
  }
};

const header = computed(() => SUBMISSION_TYPE_CONTENT.HEADERS.TITLE);

const { isNavigating, handleNavigation } = usePageNavigation('submission-type', SUBMISSION_TYPE_CONTENT.ERROR_MESSAGE);
const { isLoading } = useStoreOperation();

const submissionOptions = SUBMISSION_TYPE_CONTENT.OPTIONS.map(option => ({
    ...option,
    id: createElementId(Page.SUBMISSION_TYPE, ElementType[option.label.toUpperCase() as keyof typeof ElementType])
}));

const validationSchema = toTypedSchema(
    object({
        submissionType: number({
            required_error: SUBMISSION_TYPE_CONTENT.VALIDATION.OPTION_REQUIRED
        })
    })
);

const { values, setFieldValue } = useForm<SubmissionTypeFormValues>({
    validationSchema,
    initialValues: {
        submissionType: store.form.submissionTypeId ?? -1
    }
});

type SubmissionTypeFormValues = {
    submissionType: number;
};

const isProcessingSelection = ref(false);

const handleSelection = async (value: number) => {
    console.log('🔄 handleSelection called with value:', value);
    
    if (isLoading.value || isNavigating.value || isProcessingSelection.value) {
        console.log('⛔ Blocked by loading/navigating/processing state');
        return;
    }
    
    try {
        isProcessingSelection.value = true;
        
        // Force immediate update of the model value
        await setFieldValue('submissionType', value);
        await store.form.updateFormValue([value, 'submissionTypeId']);
        
        // Add a small delay to ensure the UI updates before navigation
        await new Promise(resolve => setTimeout(resolve, 50));
        
        console.log('📝 Form values updated, submissionTypeId:', store.form.submissionTypeId);
        console.log('Current form values:', values);

        if (value === 0) {
            console.log('🚗 Setting form type to direct');
            store.progress.setFormType('direct');
            
            // Set vehicleTypeId to 40 (car) when "Finance My Next Car" is selected
            await store.form.updateFormValue([40, 'vehicleTypeId']);
            console.log('🚗 Set vehicleTypeId to 40 (car)');
            
            console.log('🔄 Navigating to driving-licence');
            await router.push({ name: 'driving-licence' });
        } else if (value === 1) {
            console.log('🔄 Setting form type to refinance');
            store.progress.setFormType('refinance');
            
            // Set vehicleTypeId to 40 (car) when "Refinance My Current Car" is selected
            await store.form.updateFormValue([40, 'vehicleTypeId']);
            console.log('🚗 Set vehicleTypeId to 40 (car)');
            
            console.log('🔄 Navigating to refinance-form');
            await router.push({ name: 'refinance-form' });
        }
    } catch (error) {
        console.error('❌ Selection error:', error);
    } finally {
        isProcessingSelection.value = false;
    }
};

// Add a watcher to debug value changes
watch(() => values.submissionType, (newValue) => {
    console.log('Form value changed:', newValue);
});

// Add a watcher to debug store value changes
watch(() => store.form.submissionTypeId, (newValue) => {
    console.log('Store value changed:', newValue);
});

onMounted(() => {
    store.progress.setPageName('submission-type');
    store.progress.setFormType('submissionType');
});
</script>
